import * as React from "react";
import { useContext, useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import isNil from "lodash/isNil";
import isNaN from "lodash/isNaN";
import { Trans, useTranslation } from "react-i18next";
import CustomHighlighting from "views/components/highlighting/CustomHighlighting";
import DecoratedValue from "views/components/messagelist/decoration/DecoratedValue";
import TypeSpecificValue from "views/components/TypeSpecificValue";
import FieldType from "views/logic/fieldtypes/FieldType";
import LoadMoreRow from "logview/components/LoadMoreRow";
import { isInfiniteScrollUp } from "logview/helpers";
import RowCheckbox from "components/common/EntityDataTable/RowCheckbox";
import BulkSelectCell from "logview/components/BulkSelectCell";
import LogViewRowActions from "logview/components/LogViewRowActions";
import ScrollPositionContext from "logview/components/contexts/ScrollPositionContext";
import OverlayTrigger from "components/common/OverlayTrigger";
import useSelectedMessageEntities from "views/hooks/useSelectedMessageEntities";
import onTableScroll from "./OnTableScroll";
import LogViewRow from "./LogViewRow";
import LogViewCell from "./LogViewCell";
import LogViewHeader from "./LogViewHeader";
import ListStateContext from "./contexts/ListStateContext";
import MessageDetailsContext from "./contexts/MessageDetailsContext";
import { CELL_SEP } from "../Constants";
const isNullish = (v) => isNaN(v) || isNil(v);
export const LOADING_ROW_HEIGHT = 40;
const compareMessage = ({ frontendId, index: glIndex }, message) => frontendId === (message == null ? void 0 : message.frontendId) && glIndex === (message == null ? void 0 : message.index);
const Table = styled.div(
  ({ $showScrollbar }) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-anchor: none;
    overflow-y: ${$showScrollbar ? "scroll" : "visible"};
    position: relative;
  `
);
const TableInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  /* Remove bottom border of last row of last page */
  > *:last-child > *:last-child {
    border-bottom: 0;
  }
`;
const isDecoratedField = (field, decorationStats) => decorationStats && (decorationStats.added_fields[field] !== void 0 || decorationStats.changed_fields[field] !== void 0);
const fieldType = (fieldName, { decoration_stats: decorationStats }, fields) => isDecoratedField(fieldName, decorationStats) ? FieldType.Decorated : (fields && fields.find((f) => f.name === fieldName) || { type: FieldType.Unknown }).type;
const Message = ({
  tableHeader,
  setActiveMessageDetailsState,
  highlight,
  completeMessage,
  fields,
  displayBulkSelectCol = false,
  isEntitySelectable
}) => {
  const { message, frontendId, index } = completeMessage;
  const { toggleEntitySelect, isEntitySelected } = useSelectedMessageEntities();
  const { t } = useTranslation("enterprise_search");
  const isSelected = isEntitySelected(index, frontendId);
  const title = isSelected ? t("log_view_table.deselect_message") : t("log_view_table.select_message");
  const isSelectDisabled = useMemo(
    () => !(displayBulkSelectCol && isEntitySelectable(completeMessage)),
    [displayBulkSelectCol, isEntitySelectable, completeMessage]
  );
  const onChangeSelection = () => toggleEntitySelect(index, frontendId);
  return /* @__PURE__ */ React.createElement(
    LogViewRow,
    {
      key: `log-view-row-${frontendId}`,
      highlight,
      actions: /* @__PURE__ */ React.createElement(
        LogViewRowActions,
        {
          message,
          onShowDetails: () => {
            setActiveMessageDetailsState({
              frontendId,
              glIndex: completeMessage == null ? void 0 : completeMessage.index
            });
          }
        }
      )
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, displayBulkSelectCol && /* @__PURE__ */ React.createElement(BulkSelectCell, null, /* @__PURE__ */ React.createElement(
      OverlayTrigger,
      {
        trigger: "hover",
        placement: "bottom",
        disabled: !isSelectDisabled,
        overlay: /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "log_view_table.bulk_retrieval_not_supported", components: { bold: /* @__PURE__ */ React.createElement("b", null) } })
      },
      /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
        RowCheckbox,
        {
          onChange: onChangeSelection,
          title: !isSelectDisabled && title,
          checked: isSelected,
          disabled: isSelectDisabled,
          "aria-label": title
        }
      ))
    )), tableHeader.map((fieldName) => {
      const value = message[fieldName];
      return /* @__PURE__ */ React.createElement(React.Fragment, { key: `log-view-cell-${frontendId}-${fieldName}` }, /* @__PURE__ */ React.createElement(LogViewCell, { fieldName }, !isNullish(value) && /* @__PURE__ */ React.createElement(CustomHighlighting, { field: fieldName, value }, /* @__PURE__ */ React.createElement(
        TypeSpecificValue,
        {
          value,
          field: fieldName,
          type: fieldType(fieldName, completeMessage, fields),
          render: DecoratedValue
        }
      ))), fieldName !== tableHeader.last() ? CELL_SEP : null);
    }).toArray())
  );
};
const LogViewTable = ({
  columns,
  tableRef,
  pageRefs,
  fields,
  infiniteScrollDirection,
  isEntitySelectable = void 0,
  displayBulkSelectCol = false,
  bottomSection = void 0,
  onOpenMessageDetails = void 0,
  hasExecutedSearch = true,
  placeholder = void 0,
  tableHeaderRef
}) => {
  const {
    actions: { loadNextPage, loadPrevPage },
    bottomPageId,
    loadedAllPrevMessages,
    pages,
    loadedMessagesCount,
    loadPrevPromiseRef
  } = useContext(ListStateContext);
  const { finishedScrollPositionUpdateRef } = useContext(ScrollPositionContext);
  const { setActiveMessageDetailsState, activeMessageDetails } = useContext(MessageDetailsContext);
  const _setActiveMessageDetailsState = useCallback(
    ({ frontendId, glIndex }) => {
      setActiveMessageDetailsState({ frontendId, glIndex });
      if (typeof onOpenMessageDetails === "function") {
        onOpenMessageDetails();
      }
    },
    [onOpenMessageDetails, setActiveMessageDetailsState]
  );
  const _isInfiniteScrollUp = isInfiniteScrollUp(infiniteScrollDirection);
  const _onTableScroll = () => onTableScroll({
    finishedScrollPositionUpdateRef,
    infiniteScrollDirection,
    loadPrevPromiseRef,
    onBottomReach: loadNextPage,
    onTopReach: loadPrevPage,
    tableRef
  });
  const sortedPages = isInfiniteScrollUp(infiniteScrollDirection) ? pages : pages.reverse();
  return /* @__PURE__ */ React.createElement(Table, { ref: tableRef, onScroll: _onTableScroll, $showScrollbar: hasExecutedSearch }, /* @__PURE__ */ React.createElement(TableInner, null, /* @__PURE__ */ React.createElement(LogViewHeader, { columns, displayBulkSelectCol, ref: tableHeaderRef }), placeholder, _isInfiniteScrollUp && !loadedAllPrevMessages && /* @__PURE__ */ React.createElement(LoadMoreRow, { infiniteScrollDirection }), sortedPages.map(([pageId, messages]) => /* @__PURE__ */ React.createElement(
    "div",
    {
      ref: (ref) => {
        pageRefs.current[pageId] = ref;
      },
      key: `page-${pageId}`
    },
    messages.map((message) => /* @__PURE__ */ React.createElement(
      Message,
      {
        key: `${message.index}-${message == null ? void 0 : message.frontendId}`,
        tableHeader: columns,
        fields,
        highlight: compareMessage(message, activeMessageDetails),
        setActiveMessageDetailsState: _setActiveMessageDetailsState,
        completeMessage: message,
        displayBulkSelectCol,
        isEntitySelectable
      }
    ))
  )).toArray(), !_isInfiniteScrollUp && !loadedAllPrevMessages && /* @__PURE__ */ React.createElement(LoadMoreRow, { infiniteScrollDirection }), bottomSection == null ? void 0 : bottomSection(loadedMessagesCount, bottomPageId)));
};
export default LogViewTable;
